import { Delete, Edit } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useState } from "react";
import {
	Confirm,
	useDelete,
	useLocale,
	useNotify,
	useTranslate,
} from "react-admin";
import { providerIcons } from "../oauth_providers/icons";
import styles from "./ModerableSourceCard.module.scss";
import type { ModerableSource } from "./moderableSource";

dayjs.extend(relativeTime);

export function ModerableSourceCard({
	moderableSourceGroup,
}: {
	moderableSourceGroup: {
		provider: ModerableSource["provider"];
		accountId: ModerableSource["account_id"];
		sources: ModerableSource[];
	};
}) {
	const queryClient = useQueryClient();
	const notify = useNotify();
	const locale = useLocale();
	const translate = useTranslate();

	const [removeModerableSource, { isPending: isDeleting }] = useDelete(
		"moderable_sources",
		{
			id: moderableSourceGroup.sources[0].id,
		},
		{
			onSuccess: () => {
				Promise.all([
					queryClient.invalidateQueries({
						queryKey: ["moderable_sources"],
					}),

					queryClient.invalidateQueries({
						queryKey: ["posts"],
					}),
				]);

				notify("Successfully removed.", {
					type: "success",
				});
			},

			onError: () =>
				notify("Something went wrong, please try again later.", {
					type: "error",
				}),
		},
	);

	const [isConfirmOpen, setConfirmOpen] = useState(false);

	const handleCloseConfirm = () => {
		setConfirmOpen(false);
	};

	const handleConfirm = () => {
		setConfirmOpen(false);
		removeModerableSource();
	};

	const handleOpenConfirm = () => {
		setConfirmOpen(true);
	};

	return (
		<div>
			{moderableSourceGroup.sources.length > 1 ? (
				<Box
					className={`${styles.card} ${styles.card_multi}`}
					data-status={moderableSourceGroup.sources[0].status}
				>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={2}
						width="100%"
					>
						<img
							src={providerIcons[moderableSourceGroup.provider]}
							width={30}
							alt=""
						/>

						<Typography textTransform="capitalize">
							{moderableSourceGroup.provider}
						</Typography>

						{isDeleting ? (
							<CircularProgress size={24} sx={{ marginLeft: "auto" }} />
						) : (
							<Box display="flex" gap={2} marginLeft="auto">
								<button type="button" style={{ marginLeft: "auto" }}>
									<Edit
										sx={{
											color: "var(--clr-neutral-0)",
										}}
									/>
								</button>

								<button
									type="button"
									onClick={handleOpenConfirm}
									style={{ marginLeft: "auto" }}
								>
									<Delete
										sx={{
											color: "var(--clr-neutral-0)",
										}}
									/>
								</button>
							</Box>
						)}
					</Stack>

					{moderableSourceGroup.sources.map((source) => (
						<Stack key={source.id} direction="row" alignItems="center" gap={2}>
							<Box
								sx={{
									width: 40,
									height: 40,
									background: "var(--clr-neutral-800)",
									borderRadius: "50%",
									display: "grid",
									placeItems: "center",
								}}
							>
								{source.name.slice(0, 1).toUpperCase()}
							</Box>

							<Stack>
								<h3>
									<a href={source.url} target="_blank" rel="noreferrer">
										{source.name}
									</a>
								</h3>
							</Stack>
						</Stack>
					))}
				</Box>
			) : (
				<Box
					className={styles.card}
					data-status={moderableSourceGroup.sources[0].status}
				>
					<img src={providerIcons[moderableSourceGroup.provider]} alt="" />

					<Stack>
						<h3>
							<a
								href={moderableSourceGroup.sources[0].url}
								target="_blank"
								rel="noreferrer"
							>
								{moderableSourceGroup.sources[0].name}
							</a>
						</h3>
					</Stack>

					{isDeleting ? (
						<CircularProgress size={24} />
					) : (
						<button
							type="button"
							onClick={handleOpenConfirm}
							style={{ marginLeft: "auto" }}
						>
							<Delete
								sx={{
									color: "var(--clr-neutral-0)",
								}}
							/>
						</button>
					)}
				</Box>
			)}
			<Confirm
				isOpen={isConfirmOpen}
				title={translate("pos.social_networks.actions.remove_source.title", {
					provider: moderableSourceGroup.provider,
				})}
				content={translate("pos.social_networks.actions.remove_source.content")}
				onConfirm={handleConfirm}
				onClose={handleCloseConfirm}
				confirm={translate("pos.social_networks.actions.remove_source.confirm")}
				cancel={translate("pos.social_networks.actions.remove_source.cancel")}
			/>
		</div>
	);
}
